/**
 * Hook for @ mention detection and tag management in contentEditable
 */
import { useState, useEffect, useCallback } from "react";
import type { RefObject } from "react";

/**
 * Get the text offset of a node and offset within the editor
 */
function getTextOffset(editor: HTMLElement, node: Node, offset: number): number {
    const walker = document.createTreeWalker(
        editor,
        NodeFilter.SHOW_TEXT,
        null
    );
    
    let textNode: Node | null = null;
    let currentIndex = 0;
    
    while ((textNode = walker.nextNode())) {
        if (textNode === node) {
            return currentIndex + offset;
        }
        currentIndex += (textNode.textContent || "").length;
    }
    
    return currentIndex;
}

/**
 * Highlight the @ character by wrapping it in a span with mention-trigger class
 * This function finds and wraps the @ character that triggers the mention
 */
function highlightAtCharacter(editor: HTMLElement | null, atIndex: number) {
    if (!editor) return;
    
    try {
        // Get all text content and find the @ at the specified index
        const allText = editor.textContent || "";
        if (atIndex < 0 || atIndex >= allText.length || allText[atIndex] !== "@") {
            return;
        }
        
        // Find the text node containing the @
        const walker = document.createTreeWalker(
            editor,
            NodeFilter.SHOW_TEXT,
            {
                acceptNode: (node) => {
                    // Skip nodes that are inside mention-trigger spans
                    let parent = node.parentNode;
                    while (parent && parent !== editor) {
                        if ((parent as HTMLElement).classList?.contains("mention-trigger")) {
                            return NodeFilter.FILTER_REJECT;
                        }
                        parent = parent.parentNode;
                    }
                    return NodeFilter.FILTER_ACCEPT;
                }
            }
        );
        
        let textNode: Node | null = null;
        let currentIndex = 0;
        
        // Find the text node containing the @ at the specified index
        while ((textNode = walker.nextNode())) {
            const textContent = textNode.textContent || "";
            const nodeLength = textContent.length;
            
            if (atIndex >= currentIndex && atIndex < currentIndex + nodeLength) {
                // Found the text node containing the @
                const localIndex = atIndex - currentIndex;
                
                if (textContent[localIndex] === "@") {
                    // Check if @ is already wrapped
                    const parent = textNode.parentNode;
                    if (parent && (parent as HTMLElement).classList?.contains("mention-trigger")) {
                        return; // Already highlighted
                    }
                    
                    // Split the text node at @ position
                    const beforeAt = textContent.substring(0, localIndex);
                    const atChar = textContent.substring(localIndex, localIndex + 1);
                    const afterAt = textContent.substring(localIndex + 1);
                    
                    // Create new nodes
                    const beforeNode = beforeAt ? document.createTextNode(beforeAt) : null;
                    const atSpan = document.createElement("span");
                    atSpan.className = "mention-trigger";
                    atSpan.textContent = atChar;
                    const afterNode = afterAt ? document.createTextNode(afterAt) : null;
                    
                    // Replace in DOM
                    const parentNode = textNode.parentNode;
                    if (parentNode) {
                        if (beforeNode) {
                            parentNode.insertBefore(beforeNode, textNode);
                        }
                        parentNode.insertBefore(atSpan, textNode);
                        if (afterNode) {
                            parentNode.insertBefore(afterNode, textNode);
                        }
                        parentNode.removeChild(textNode);
                    }
                }
                break;
            }
            
            currentIndex += nodeLength;
        }
    } catch (error) {
        // Silently fail if DOM manipulation fails
        console.debug("Failed to highlight @ character:", error);
    }
}

interface MentionState {
    isActive: boolean;
    query: string;
    startPos: number;
    endPos: number;
}

interface UseDocumentMentionResult extends MentionState {
    insertDocument: (documentName: string, documentId: string) => void;
    removeDocument: (documentId: string) => void;
    clearMention: () => void;
}

export function useDocumentMention(
    editorRef: RefObject<HTMLDivElement | null>
): UseDocumentMentionResult {
    const [mentionState, setMentionState] = useState<MentionState>({
        isActive: false,
        query: "",
        startPos: -1,
        endPos: -1,
    });

    useEffect(() => {
        if (!editorRef.current) return;

        const handleInput = () => {
            const selection = window.getSelection();
            if (!selection || selection.rangeCount === 0) return;

            const range = selection.getRangeAt(0);
            
            // Get the text content of the entire editor to find @ position
            const editor = editorRef.current;
            if (!editor) return;
            
            const allText = editor.textContent || "";
            const rangeStart = getTextOffset(editor, range.startContainer, range.startOffset);
            
            // Find the last @ before cursor
            const textBeforeCursor = allText.substring(0, rangeStart);
            const lastAtIndex = textBeforeCursor.lastIndexOf("@");

            if (lastAtIndex === -1) {
                setMentionState({
                    isActive: false,
                    query: "",
                    startPos: -1,
                    endPos: -1,
                });
                return;
            }

            const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);

            // If there's a space, close mention
            if (textAfterAt.includes(" ")) {
                setMentionState({
                    isActive: false,
                    query: "",
                    startPos: -1,
                    endPos: -1,
                });
                return;
            }

            // @ must be at start or preceded by space
            const charBeforeAt =
                lastAtIndex > 0 ? allText[lastAtIndex - 1] : " ";

            if (charBeforeAt !== " " && lastAtIndex !== 0) {
                setMentionState({
                    isActive: false,
                    query: "",
                    startPos: -1,
                    endPos: -1,
                });
                return;
            }

            // Activate mention
            setMentionState({
                isActive: true,
                query: textAfterAt,
                startPos: lastAtIndex,
                endPos: rangeStart,
            });
            
            // Highlight the @ character by wrapping it in a span
            // Use setTimeout to avoid interfering with the input event
            setTimeout(() => {
                highlightAtCharacter(editorRef.current, lastAtIndex);
            }, 0);
        };

        const editor = editorRef.current;
        editor.addEventListener("input", handleInput);
        editor.addEventListener("keyup", handleInput);
        editor.addEventListener("click", handleInput);

        return () => {
            editor.removeEventListener("input", handleInput);
            editor.removeEventListener("keyup", handleInput);
            editor.removeEventListener("click", handleInput);
        };
    }, [editorRef]);

    const insertDocument = useCallback((documentName: string, documentId: string) => {
        if (!editorRef.current || !mentionState.isActive) return;

        const editor = editorRef.current;
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        // Create the tag element
        const tagElement = document.createElement("span");
        tagElement.contentEditable = "false";
        tagElement.className = "inline-document-tag";
        tagElement.dataset.documentId = documentId;
        tagElement.dataset.documentName = documentName;

        // Inner HTML of the tag
        tagElement.innerHTML = `
            <span class="tag-content">
                <svg class="tag-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                          d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                <span class="tag-name">${documentName}</span>
                <button class="tag-remove" type="button" data-document-id="${documentId}">
                    <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" 
                              d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </span>
        `;

        // First, try to find the mention-trigger span (the @ character)
        const mentionTrigger = editor.querySelector(".mention-trigger");
        
        if (mentionTrigger) {
            // @ is already wrapped in a span, replace it and the following text
            const range = document.createRange();
            range.setStartBefore(mentionTrigger);
            
            // Find the end position: after the query text
            const endPos = mentionState.endPos;
            
            // Find the node at endPos
            const walker = document.createTreeWalker(
                editor,
                NodeFilter.SHOW_TEXT,
                null
            );
            
            let textNode: Node | null = null;
            let currentIndex = 0;
            let endNode: Node | null = null;
            let endOffset = 0;
            
            while ((textNode = walker.nextNode())) {
                const textContent = textNode.textContent || "";
                const nodeLength = textContent.length;
                
                if (currentIndex + nodeLength >= endPos) {
                    endNode = textNode;
                    endOffset = endPos - currentIndex;
                    break;
                }
                
                currentIndex += nodeLength;
            }
            
            if (endNode) {
                range.setEnd(endNode, endOffset);
            } else {
                // Fallback: end after the mention trigger and query
                range.setEndAfter(mentionTrigger);
                // Try to find text after the mention trigger
                let nextNode = mentionTrigger.nextSibling;
                while (nextNode && nextNode.nodeType !== Node.TEXT_NODE) {
                    nextNode = nextNode.nextSibling;
                }
                if (nextNode && nextNode.nodeType === Node.TEXT_NODE) {
                    const textNode = nextNode as Text;
                    const queryLength = mentionState.query.length;
                    if (textNode.textContent && textNode.textContent.length >= queryLength) {
                        range.setEnd(textNode, queryLength);
                    }
                }
            }
            
            // Remove the @query text and insert the tag
            range.deleteContents();
            range.insertNode(tagElement);
        } else {
            // @ is not wrapped, find it by text position
            const walker = document.createTreeWalker(
                editor,
                NodeFilter.SHOW_TEXT,
                null
            );

            let textNode: Node | null = null;
            let currentIndex = 0;
            let foundStart = false;
            let startNode: Node | null = null;
            let startOffset = 0;
            let endNode: Node | null = null;
            let endOffset = 0;

            // Find the start and end positions
            while ((textNode = walker.nextNode())) {
                const textContent = textNode.textContent || "";
                const nodeLength = textContent.length;

                // Check if startPos is in this node
                if (!foundStart && currentIndex + nodeLength >= mentionState.startPos) {
                    foundStart = true;
                    startNode = textNode;
                    startOffset = mentionState.startPos - currentIndex;
                }

                // Check if endPos is in this node
                if (foundStart && currentIndex + nodeLength >= mentionState.endPos) {
                    endNode = textNode;
                    endOffset = mentionState.endPos - currentIndex;
                    break;
                }

                currentIndex += nodeLength;
            }

            if (startNode && endNode) {
                // We found the exact range, replace it
                const range = document.createRange();
                range.setStart(startNode, startOffset);
                range.setEnd(endNode, endOffset);
                
                // Remove the @query text and insert the tag
                range.deleteContents();
                range.insertNode(tagElement);
            } else {
                // Last fallback: use current selection
                const range = selection.getRangeAt(0);
                const container = range.startContainer;
                
                if (container.nodeType === Node.TEXT_NODE) {
                    const textContent = container.textContent || "";
                    const cursorPosition = range.startOffset;
                    const textBeforeCursor = textContent.substring(0, cursorPosition);
                    const lastAtIndex = textBeforeCursor.lastIndexOf("@");
                    
                    if (lastAtIndex !== -1) {
                        const before = textContent.substring(0, lastAtIndex);
                        const after = textContent.substring(cursorPosition);
                        
                        // Create new nodes
                        const beforeNode = before ? document.createTextNode(before) : null;
                        const spaceNode = document.createTextNode(" ");
                        const afterNode = after ? document.createTextNode(after) : null;
                        
                        // Replace in DOM
                        const parent = container.parentNode!;
                        if (beforeNode) {
                            parent.insertBefore(beforeNode, container);
                        }
                        parent.insertBefore(tagElement, container);
                        parent.insertBefore(spaceNode, container);
                        if (afterNode) {
                            parent.insertBefore(afterNode, container);
                        }
                        parent.removeChild(container);
                        
                        // Position cursor after the tag
                        const newRange = document.createRange();
                        newRange.setStartAfter(spaceNode);
                        newRange.collapse(true);
                        selection.removeAllRanges();
                        selection.addRange(newRange);
                    }
                }
            }
        }
        
        // Add a space after the tag if not already present
        if (tagElement.nextSibling?.nodeType !== Node.TEXT_NODE || 
            !tagElement.nextSibling.textContent?.startsWith(" ")) {
            const spaceNode = document.createTextNode(" ");
            tagElement.parentNode?.insertBefore(spaceNode, tagElement.nextSibling);
            
            // Position cursor after the space
            const newRange = document.createRange();
            newRange.setStartAfter(spaceNode);
            newRange.collapse(true);
            selection.removeAllRanges();
            selection.addRange(newRange);
        }

        // Reset mention state
        setMentionState({
            isActive: false,
            query: "",
            startPos: -1,
            endPos: -1,
        });

        // Trigger input event to update state
        editor.dispatchEvent(new Event("input", { bubbles: true }));
    }, [mentionState, editorRef]);

    const removeDocument = useCallback((documentId: string) => {
        if (!editorRef.current) return;

        const tag = editorRef.current.querySelector(
            `[data-document-id="${documentId}"]`
        ) as HTMLElement;

        if (tag) {
            tag.remove();
            editorRef.current.dispatchEvent(
                new Event("input", { bubbles: true })
            );
        }
    }, [editorRef]);

    const clearMention = useCallback(() => {
        setMentionState({
            isActive: false,
            query: "",
            startPos: -1,
            endPos: -1,
        });
    }, []);

    return {
        ...mentionState,
        insertDocument,
        removeDocument,
        clearMention,
    };
}
